// src/components/inventory/PurchaseOrderDetailModal.js
// Enhanced Production-Ready Purchase Order Detail Modal
// NEW FEATURE: Digital Signature & Multi-Level Approval Flow (Clerk -> Engineer -> Finance)
//
// --- UPDATE (Procurement MOW+ v1) ----------------------------------------------------------
//   • Buttons follow the server's permissions for the signed-in user: Sign only when you may sign
//     this stage (else the reason is shown — role, segregation of duties), Authorize (override)
//     for managers, Reject for the current signer / managers, Receive goods when Ordered/Shipped.
//   • Receive goods (all or part) — stock booked through the inventory movement log; items show
//     ordered / received / still to come; status "Partly received".
//   • Real timeline from the server (created, signed, rejected, emailed, received …) — it was
//     made up from the status; FlowSense AI bar explains the order and its risks (late, price vs
//     earlier orders, duplicates, supplier record).
//   • Live: reloads when this order changes anywhere (WebSocket purchase_order_update).
//   • Fixes: editing notes called purchaseOrderService.updateNotes, which did not exist; logger.warn
//     was undefined (crash when signatures failed to load); the signer was always sent as "System
//     Administrator" (the server now takes the login); Expected Delivery read a missing field;
//     "Download PDF", "Share" and "Call Supplier" did nothing; Print printed the whole app; the
//     supplier tab showed "Verified Supplier", "Net 30", "Bank Transfer" for every supplier.
// --- END UPDATE ---------------------------------------------------------------------------

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Button, Text, Spinner, Flex, useToast, useColorModeValue, Badge, Table, Thead, Tbody, Tr,
  Th, Td, Select, Box, HStack, VStack, Divider, SimpleGrid, Icon, Tooltip, IconButton, Tag,
  TagLabel, TagLeftIcon, Textarea, Menu, MenuButton, MenuList, MenuItem, MenuDivider, Tabs,
  TabList, TabPanels, Tab, TabPanel, Avatar, Collapse, useDisclosure, Skeleton, SkeletonCircle,
  TableContainer, Heading, Stepper, Step, StepIndicator, StepStatus, StepIcon, StepNumber,
  StepTitle, StepSeparator, Image, FormControl, FormLabel, AlertDialog, AlertDialogOverlay,
  AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter
} from '@chakra-ui/react';
import {
  MdShoppingCart, MdLocalShipping, MdInventory, MdAttachMoney, MdDateRange, MdPerson,
  MdEmail, MdPhone, MdLocationOn, MdPrint, MdDownload, MdEdit, MdSave, MdCancel,
  MdCheckCircle, MdMoreVert, MdWarning, MdInfo, MdHistory, MdTimeline, MdNote, MdContentCopy,
  MdShare, MdReceipt, MdPending, MdDone, MdExpandMore, MdExpandLess, MdStore,
  MdVerified, MdThumbUp, MdDraw
} from 'react-icons/md';

import { motion, AnimatePresence } from 'framer-motion';
import purchaseOrderService from '../../services/purchaseOrderService';
// --- UPDATE (Procurement MOW+ v1)
import { Alert, AlertIcon } from '@chakra-ui/react';
import { MdLock } from 'react-icons/md';
import { poErrorMessage } from '../../services/purchaseOrderService';
import useInventoryLive from '../../hooks/useInventoryLive';
import FlowSenseInsightBar from './FlowSenseInsightBar';
import ReceiveGoodsModal from './ReceiveGoodsModal';
import { explainPO, explainProcurementForMinistry, buildProcurementContext, poPrintHtml, usd } from './procurementAiIntel';
// --- END UPDATE

const MotionBox = motion(Box);
const MotionTr = motion(Tr);
// --- UPDATE (Procurement MOW+ v1): timeline event labels
const EVENT_META = {
  created: { icon: MdShoppingCart, color: 'blue', title: 'Order created' }, signed: { icon: MdDraw, color: 'purple', title: 'Signed' },
  approved: { icon: MdCheckCircle, color: 'green', title: 'Approved' }, rejected: { icon: MdCancel, color: 'red', title: 'Rejected' },
  emailed: { icon: MdEmail, color: 'teal', title: 'Emailed to supplier' }, email_failed: { icon: MdWarning, color: 'orange', title: 'Email failed' },
  email_skipped: { icon: MdInfo, color: 'gray', title: 'Email not sent' }, partially_received: { icon: MdInventory, color: 'purple', title: 'Partly received' },
  received: { icon: MdInventory, color: 'green', title: 'Received into stock' }, notes: { icon: MdNote, color: 'gray', title: 'Notes updated' },
  status_shipped: { icon: MdLocalShipping, color: 'cyan', title: 'Shipped' }, status_cancelled: { icon: MdCancel, color: 'red', title: 'Cancelled' }, status_ordered: { icon: MdShoppingCart, color: 'blue', title: 'Ordered' },
};
// --- END UPDATE

// ============================================================================
// DEBUG LOGGER
// ============================================================================
const DEBUG_ENABLED = process.env.NODE_ENV === 'development';

const logger = {
  info: (message, data) => {
    if (!DEBUG_ENABLED) return;
    console.log(`%c[PODetailModal] ℹ️ ${message}`, 'color: #3182ce', data || '');
  },
  error: (message, error) => {
    if (!DEBUG_ENABLED) return;
    console.error(`%c[PODetailModal] ❌ ${message}`, 'color: #e53e3e', error || '');
  },
  success: (message, data) => {
    if (!DEBUG_ENABLED) return;
    console.log(`%c[PODetailModal] ✅ ${message}`, 'color: #38a169', data || '');
  },
  // --- UPDATE: warn was called but missing (TypeError when signatures could not load)
  warn: (message, data) => {
    if (!DEBUG_ENABLED) return;
    console.warn(`[PODetailModal] ⚠️ ${message}`, data || '');
  },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount || 0);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusConfig = (status) => {
  const configs = {
    'Pending': { color: 'yellow', icon: MdPending, label: 'Pending', step: 1 },
    'Ordered': { color: 'blue', icon: MdShoppingCart, label: 'Ordered', step: 2 },
    'Shipped': { color: 'cyan', icon: MdLocalShipping, label: 'Shipped', step: 3 },
    'Received': { color: 'green', icon: MdDone, label: 'Received', step: 4 },
    'Cancelled': { color: 'red', icon: MdCancel, label: 'Cancelled', step: 0 },
    'Partial': { color: 'orange', icon: MdWarning, label: 'Partial', step: 3 },
  };
  return configs[status] || { color: 'gray', icon: MdInfo, label: status, step: 0 };
};

// ============================================================================
// DIGITAL SIGNATURE PAD COMPONENT (Native HTML5 Canvas)
// ============================================================================
const DigitalSignaturePad = ({ onSign, onCancel, currentStage }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [comments, setComments] = useState('');
  
  // FIX: Hooks at top level
  const borderColor = useColorModeValue('gray.300', 'gray.600');
  const bg = useColorModeValue('gray.50', 'gray.900');
  const wrapperBg = useColorModeValue('white', 'gray.800');
  const strokeColor = useColorModeValue('#000', '#fff'); // Black ink for light mode, white for dark

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.strokeStyle = strokeColor; 
    }
  }, [strokeColor]); // Reacting to stroke color changes safely

  const startDrawing = ({ nativeEvent }) => {
    const { offsetX, offsetY } = nativeEvent;
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(offsetX, offsetY);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = ({ nativeEvent }) => {
    if (!isDrawing) return;
    const { offsetX, offsetY } = nativeEvent;
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(offsetX, offsetY);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
        const ctx = canvasRef.current.getContext('2d');
        ctx.closePath();
        setIsDrawing(false);
    }
  };

  // Support for touch devices
  const getTouchPos = (canvas, touchEvent) => {
    const rect = canvas.getBoundingClientRect();
    return {
      offsetX: touchEvent.touches[0].clientX - rect.left,
      offsetY: touchEvent.touches[0].clientY - rect.top
    };
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const handleSave = () => {
    if (!hasDrawn) return;
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onSign(dataUrl, comments);
  };

  return (
    <VStack spacing={4} align="stretch" w="full" bg={wrapperBg} p={5} borderRadius="xl" borderWidth="1px" borderColor="purple.200" boxShadow="lg">
      <HStack color="purple.500">
        <Icon as={MdDraw} boxSize={5} />
        <Heading size="sm">Digital Signature Required</Heading>
      </HStack>
      <Text fontSize="sm" color="gray.500">Sign below to authorize this document as <strong>{currentStage}</strong>.</Text>
      
      <Box border="2px dashed" borderColor={borderColor} borderRadius="lg" bg={bg} overflow="hidden" position="relative">
        <canvas
          ref={canvasRef}
          width={800} 
          height={200}
          style={{ width: '100%', height: '200px', cursor: 'crosshair', touchAction: 'none' }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseOut={stopDrawing}
          onTouchStart={(e) => {
            e.preventDefault();
            const pos = getTouchPos(canvasRef.current, e);
            startDrawing({ nativeEvent: pos });
          }}
          onTouchMove={(e) => {
            e.preventDefault();
            const pos = getTouchPos(canvasRef.current, e);
            draw({ nativeEvent: pos });
          }}
          onTouchEnd={stopDrawing}
        />
        {!hasDrawn && (
          <Text position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" color="gray.300" pointerEvents="none" fontWeight="bold" fontSize="2xl" opacity={0.5}>
            Sign Here
          </Text>
        )}
      </Box>

      <FormControl>
        <FormLabel fontSize="xs" fontWeight="bold">Approval Notes (Optional)</FormLabel>
        <Textarea 
            value={comments} 
            onChange={(e) => setComments(e.target.value)} 
            size="sm" 
            borderRadius="lg" 
            placeholder="Add any conditions, budget confirmations, or notes regarding this approval..." 
        />
      </FormControl>

      <HStack justify="space-between" pt={2}>
        <Button size="sm" variant="ghost" onClick={handleClear} isDisabled={!hasDrawn}>Clear Pad</Button>
        <HStack>
          <Button size="sm" variant="outline" onClick={onCancel} borderRadius="xl">Cancel</Button>
          <Button size="sm" colorScheme="purple" leftIcon={<Icon as={MdCheckCircle} />} isDisabled={!hasDrawn} onClick={handleSave} borderRadius="xl">
            Apply Digital Signature
          </Button>
        </HStack>
      </HStack>
    </VStack>
  );
};

// ============================================================================
// ANIMATED STATUS BADGE
// ============================================================================
const AnimatedStatusBadge = ({ status, size = 'md' }) => {
  const config = getStatusConfig(status);
  
  return (
    <MotionBox
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      whileHover={{ scale: 1.05 }}
    >
      <Tag
        size={size}
        colorScheme={config.color}
        borderRadius="full"
        px={4}
        py={2}
      >
        <TagLeftIcon as={config.icon} boxSize={4} />
        <TagLabel fontWeight="700" fontSize={size === 'lg' ? 'md' : 'sm'}>
          {config.label}
        </TagLabel>
      </Tag>
    </MotionBox>
  );
};

// ============================================================================
// STATUS PROGRESS TRACKER
// ============================================================================
const StatusProgressTracker = ({ currentStatus }) => {
  const config = getStatusConfig(currentStatus);
  const steps = ['Pending', 'Ordered', 'Shipped', 'Received'];
  const isCancelled = currentStatus === 'Cancelled';
  
  // All hooks must be called before any conditional returns
  const activeBg = useColorModeValue('blue.500', 'blue.400');
  const inactiveBg = useColorModeValue('gray.200', 'gray.600');
  const completedBg = useColorModeValue('green.500', 'green.400');
  const cancelledBg = useColorModeValue('red.50', 'red.900');
  const cancelledBorderColor = useColorModeValue('red.200', 'red.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');

  if (isCancelled) {
    return (
      <MotionBox
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        p={4}
        bg={cancelledBg}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={cancelledBorderColor}
      >
        <HStack justify="center" spacing={3}>
          <Icon as={MdCancel} color="red.500" boxSize={6} />
          <Text color="red.500" fontWeight="bold">
            This order has been cancelled
          </Text>
        </HStack>
      </MotionBox>
    );
  }

  return (
    <Box w="full">
      <Flex justify="space-between" align="center" position="relative">
        {/* Progress Line */}
        <Box
          position="absolute"
          top="20px"
          left="10%"
          right="10%"
          h="4px"
          bg={inactiveBg}
          borderRadius="full"
          zIndex={0}
        />
        <Box
          position="absolute"
          top="20px"
          left="10%"
          w={`${Math.min((config.step - 1) / 3 * 80, 80)}%`}
          h="4px"
          bg={completedBg}
          borderRadius="full"
          zIndex={1}
          transition="width 0.5s ease"
        />

        {steps.map((step, index) => {
          const stepNum = index + 1;
          const isCompleted = config.step > stepNum;
          const isActive = config.step === stepNum;
          
          return (
            <VStack key={step} spacing={2} zIndex={2} flex={1}>
              <MotionBox
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: index * 0.1 }}
              >
                <Flex
                  w="40px"
                  h="40px"
                  borderRadius="full"
                  bg={isCompleted ? completedBg : isActive ? activeBg : inactiveBg}
                  justify="center"
                  align="center"
                  boxShadow={isActive ? 'lg' : 'sm'}
                  transition="all 0.3s"
                >
                  <Icon
                    as={isCompleted ? MdCheckCircle : getStatusConfig(step).icon}
                    color="white"
                    boxSize={5}
                  />
                </Flex>
              </MotionBox>
              <Text
                fontSize="xs"
                fontWeight={isActive ? 'bold' : 'medium'}
                color={isActive ? activeBg : textColor}
                textAlign="center"
              >
                {step}
              </Text>
            </VStack>
          );
        })}
      </Flex>
    </Box>
  );
};

// ============================================================================
// INFO CARD COMPONENT
// ============================================================================
const InfoCard = ({ icon, label, value, color = 'blue', isLoading }) => {
  const cardBg = useColorModeValue('white', 'gray.700');
  const iconBg = useColorModeValue(`${color}.50`, `${color}.900`);
  const iconColor = useColorModeValue(`${color}.500`, `${color}.300`);
  const labelColor = useColorModeValue('gray.500', 'gray.400');
  const valueColor = useColorModeValue('gray.800', 'white');

  return (
    <MotionBox
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2 }}
    >
      <HStack
        bg={cardBg}
        p={4}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={useColorModeValue('gray.100', 'gray.600')}
        spacing={4}
        _hover={{
          borderColor: `${color}.300`,
          boxShadow: 'md',
        }}
        transition="all 0.2s"
      >
        <Box bg={iconBg} p={3} borderRadius="lg">
          <Icon as={icon} boxSize={5} color={iconColor} />
        </Box>
        <VStack align="start" spacing={0} flex={1}>
          <Text fontSize="xs" color={labelColor} fontWeight="600" textTransform="uppercase">
            {label}
          </Text>
          {isLoading ? (
            <Skeleton height="20px" width="80px" />
          ) : (
            <Text fontSize="md" fontWeight="bold" color={valueColor} noOfLines={1}>
              {value}
            </Text>
          )}
        </VStack>
      </HStack>
    </MotionBox>
  );
};

// ============================================================================
// TIMELINE ITEM COMPONENT
// ============================================================================
const TimelineItem = ({ icon, title, description, time, isLast, color = 'blue' }) => {
  const lineColor = useColorModeValue('gray.200', 'gray.600');
  const iconBg = useColorModeValue(`${color}.100`, `${color}.800`);
  const iconColor = useColorModeValue(`${color}.600`, `${color}.300`);
  const textColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <Flex>
      <VStack spacing={0} mr={4}>
        <Box
          bg={iconBg}
          p={2}
          borderRadius="full"
          boxShadow="sm"
        >
          <Icon as={icon} color={iconColor} boxSize={4} />
        </Box>
        {!isLast && (
          <Box w="2px" h="40px" bg={lineColor} />
        )}
      </VStack>
      <VStack align="start" spacing={1} pb={isLast ? 0 : 4}>
        <Text fontWeight="600" fontSize="sm">
          {title}
        </Text>
        <Text fontSize="xs" color={textColor}>
          {description}
        </Text>
        <Text fontSize="xs" color={textColor} fontStyle="italic">
          {time}
        </Text>
      </VStack>
    </Flex>
  );
};

// ============================================================================
// SKELETON LOADER
// ============================================================================
const DetailSkeleton = () => (
  <VStack spacing={6} align="stretch">
    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
      {[...Array(4)].map((_, i) => (
        <HStack key={i} p={4} borderRadius="xl" borderWidth="1px">
          <SkeletonCircle size="12" />
          <VStack align="start" spacing={1} flex={1}>
            <Skeleton height="12px" width="60px" />
            <Skeleton height="20px" width="100px" />
          </VStack>
        </HStack>
      ))}
    </SimpleGrid>
    <Skeleton height="60px" borderRadius="xl" />
    <Skeleton height="200px" borderRadius="xl" />
  </VStack>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const PurchaseOrderDetailModal = ({ isOpen, onClose, poId, onStatusUpdate, intel = null, insights = null }) => { // --- UPDATE: + intel (procurement AI), insights (inventory items)
  // State
  const [po, setPo] = useState(null);
  const [signatures, setSignatures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [notes, setNotes] = useState('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  
  // Signature Pad State
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  // --- UPDATE
  const [events, setEvents] = useState([]);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const isEditingNotesRef = useRef(false);
  const totalBg = useColorModeValue('green.50', 'rgba(56,161,105,0.15)');
  // --- END UPDATE
  
  // Hooks
  const toast = useToast();
  const { isOpen: isTimelineOpen, onToggle: onTimelineToggle } = useDisclosure({ defaultIsOpen: true });
  
  // Reject Dialog Disclosure
  const { isOpen: isRejectOpen, onOpen: onRejectOpen, onClose: onRejectClose } = useDisclosure();
  const cancelRef = useRef();

  // FIX: Theme colors at Top Level
  const modalBg = useColorModeValue('white', 'gray.800');
  const headerBg = useColorModeValue('gray.50', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
  const tableBg = useColorModeValue('white', 'gray.800');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.900');
  const tableHoverBg = useColorModeValue('blue.50', 'blue.900');
  const tableStripedBg = useColorModeValue('gray.50', 'gray.750');
  const sectionBg = useColorModeValue('white', 'gray.800');
  const selectBg = useColorModeValue('white', 'gray.700');
  const headerGradient = useColorModeValue(
    'linear(to-r, blue.500, teal.500)',
    'linear(to-r, blue.400, teal.400)'
  );

  // ============================================================================
  // HIERARCHY / WORKFLOW LOGIC
  // ============================================================================
  const hierarchySteps = [
    { title: 'Warehouse Clerk', desc: 'Verify Necessity' },
    { title: 'Site Engineer', desc: 'Verify Tech Specs' },
    { title: 'Financial Controller', desc: 'Release Funds' }
  ];

  const getActiveStepIndex = () => {
    if (!po) return 0;
    if (po.status !== 'Pending') return 3; // Fully approved or closed
    
    // Check current approval stage from database
    const stage = po.approval_stage;
    if (stage === 'Warehouse Clerk') return 0;
    if (stage === 'Site Engineer') return 1;
    if (stage === 'Financial Controller') return 2;
    return 3;
  };

  // ============================================================================
  // FETCH PO DETAILS & SIGNATURES
  // ============================================================================
  const fetchPODetails = useCallback(async (quiet = false) => { // --- UPDATE: + quiet (live reload without the skeleton)
    if (!poId) return;
    
    logger.info('Fetching PO details', { poId });
    if (quiet !== true) setIsLoading(true);
    
    try {
      const data = await purchaseOrderService.getPOById(poId);
      setPo(data);
      if (!isEditingNotesRef.current) setNotes(data?.notes || ''); // --- UPDATE: a live reload never overwrites notes being typed
      // --- UPDATE: real timeline
      if (Array.isArray(data?.events)) setEvents(data.events);
      else { try { setEvents((await purchaseOrderService.getEvents(poId)) || []); } catch (_) { setEvents([]); } }
      // --- END UPDATE
      logger.success('PO details loaded', { poId, data });

      // Fetch Signature Ledger
      try {
          const sigs = await purchaseOrderService.getPOSignatures(poId);
          setSignatures(sigs || []);
      } catch (e) {
          logger.warn("No signatures found or failed to load signatures");
      }

    } catch (error) {
      logger.error('Failed to fetch PO details', error);
      toast({
        title: 'Error fetching PO details',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsLoading(false);
    }
  }, [poId, toast]);

  useEffect(() => {
    if (isOpen) {
      fetchPODetails();
    }
  }, [isOpen, fetchPODetails]);

  // --- UPDATE (Procurement MOW+ v1): live reload of this order + permissions from the server
  isEditingNotesRef.current = isEditingNotes;
  useInventoryLive(() => fetchPODetails(true), { enabled: !!(isOpen && poId), events: ['purchase_order_update'], pollMs: 0, filter: (e) => e.po_id == null || String(e.po_id) === String(poId) });
  const perms = po?.permissions || null;
  const receivingById = new Map((po?.receiving || []).map((l) => [String(l.purchase_order_item_id), l]));
  const canSign = perms ? perms.can_sign : true;
  const canApprove = perms ? perms.can_approve : true;
  const canReject = perms ? perms.can_reject : true;
  const canReceive = perms ? perms.can_receive : ['Ordered', 'Shipped', 'Partial'].includes(po?.status);
  // --- END UPDATE

  // ============================================================================
  // WORKFLOW HANDLERS (STATUS, APPROVE, REJECT, SIGN)
  // ============================================================================
  
  const handleApplySignature = async (signatureData, comments) => {
    setIsUpdating(true);
    try {
        const payload = {
            signatureData,
            comments,
            // --- UPDATE: the server signs with the logged-in user (was approverName: 'System Administrator')
        };
        const res = await purchaseOrderService.signPO(po.id, payload);
        
        toast({
            title: 'Signature Applied',
            description: res.message || 'Successfully signed document.',
            status: 'success',
            duration: 4000,
            isClosable: true,
            position: 'top-right'
        });
        
        setShowSignaturePad(false);
        onStatusUpdate?.(); // --- UPDATE: optional
        fetchPODetails();
    } catch (error) {
        logger.error('Signature Failed', error);
        toast({ title: 'Signature Failed', description: poErrorMessage(error), status: 'error', duration: 6000, position: 'top-right' }); // --- UPDATE: server reason (role, segregation of duties)
    } finally {
        setIsUpdating(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!po || po.status === 'Received' || po.status === 'Cancelled') return;
    
    logger.info('Updating PO status', { poId: po.id, newStatus });
    setIsUpdating(true);
    
    try {
      await purchaseOrderService.updatePOStatus(po.id, newStatus);
      logger.success('Status updated successfully', { poId: po.id, newStatus });
      
      toast({
        title: 'Status Updated',
        description: `Order status changed to ${newStatus}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      
      onStatusUpdate();
      fetchPODetails();
    } catch (error) {
      logger.error('Failed to update status', error);
      toast({
        title: 'Error updating status',
        description: poErrorMessage(error), // --- UPDATE
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleApprove = async () => {
    if (!po) return;
    setIsUpdating(true);
    try {
      await purchaseOrderService.approvePO(po.id);
      toast({ 
          title: 'Order Approved', 
          description: 'The purchase order has been formally authorized.', 
          status: 'success', 
          duration: 3000, 
          position: 'top-right' 
      });
      onStatusUpdate();
      fetchPODetails();
    } catch (error) {
      toast({ 
          title: 'Approval Failed', 
          description: error.response?.data?.message || error.message, 
          status: 'error', 
          duration: 5000 
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleReject = async () => {
    if (!po) return;
    setIsUpdating(true);
    try {
      await purchaseOrderService.rejectPO(po.id, rejectReason);
      toast({ 
          title: 'Order Rejected', 
          description: 'The purchase order has been cancelled and returned to the initiator.', 
          status: 'warning', 
          duration: 3000, 
          position: 'top-right' 
      });
      onRejectClose();
      onStatusUpdate();
      fetchPODetails();
    } catch (error) {
      toast({ 
          title: 'Rejection Failed', 
          description: error.response?.data?.message || error.message, 
          status: 'error', 
          duration: 5000 
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // ============================================================================
  // SAVE NOTES HANDLER
  // ============================================================================
  const handleSaveNotes = async () => {
    if (!po) return;
    
    logger.info('Saving notes', { poId: po.id });
    
    try {
      await purchaseOrderService.updateNotes(po.id, notes, { replace: true }); // --- UPDATE: now exists — edits (replaces) the notes
      
      toast({
        title: 'Notes Saved',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      
      setIsEditingNotes(false);
    } catch (error) {
      logger.error('Failed to save notes', error);
      toast({
        title: 'Error saving notes',
        description: poErrorMessage(error), // --- UPDATE
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================
  const calculateTotal = () => {
    return po?.items?.reduce((acc, item) => acc + (item.quantity * item.unit_price), 0) || 0;
  };

  const calculateItemCount = () => {
    return po?.items?.reduce((acc, item) => acc + item.quantity, 0) || 0;
  };

  const handlePrint = () => {
    logger.info('Printing PO', { poId: po?.id });
    // --- UPDATE: a printable PO document (window.print() printed the whole app); "Save as PDF" in the dialog
    const w = window.open('', '_blank');
    if (!w) { toast({ title: 'Allow pop-ups to print', status: 'warning', duration: 3000 }); return; }
    w.document.write(poPrintHtml({ ...po, signatures }));
    w.document.close();
    // --- END UPDATE
    toast({
      title: 'Preparing print view...',
      status: 'info',
      duration: 2000,
    });
  };
  // --- UPDATE: share + call
  const handleShare = async () => {
    const text = `${po?.po_number || `PO #${po?.id}`} — ${po?.supplier_name}, ${usd(po?.total_amount)}, ${po?.status}`;
    try {
      if (navigator.share) await navigator.share({ title: po?.po_number || 'Purchase order', text });
      else { await navigator.clipboard.writeText(text); toast({ title: 'Summary copied', description: text, status: 'success', duration: 2500 }); }
    } catch (_) { /* cancelled */ }
  };
  const handleCallSupplier = () => {
    if (po?.supplier_phone) window.open(`tel:${po.supplier_phone}`);
    else toast({ title: 'No phone number', description: 'Supplier phone is not available', status: 'warning', duration: 3000 });
  };
  // --- END UPDATE

  const handleEmailSupplier = () => {
    logger.info('Emailing supplier', { supplier: po?.supplier_email });
    if (po?.supplier_email) {
      window.open(`mailto:${po.supplier_email}?subject=Purchase Order #${po.id}`);
    } else {
      toast({
        title: 'No email address',
        description: 'Supplier email is not available',
        status: 'warning',
        duration: 3000,
      });
    }
  };

  const handleCopyPONumber = () => {
    navigator.clipboard.writeText(`PO-${po?.id}`);
    toast({
      title: 'Copied!',
      description: `PO-${po?.id} copied to clipboard`,
      status: 'success',
      duration: 2000,
    });
  };

  // --- UPDATE: real events from the server; the derived list below is only the fallback for older servers
  const serverTimeline = events.map((e) => {
    const m = EVENT_META[e.event] || { icon: MdHistory, color: 'gray', title: String(e.event).replace(/_/g, ' ') };
    return { icon: m.icon, title: m.title, description: `${e.detail || ''}${e.user_name ? ` — ${e.user_name}` : ''}`, time: formatDateTime(e.created_at), color: m.color };
  });
  // --- END UPDATE
  // Mock timeline data (in production, this would come from the API)
  const derivedTimeline = [ // --- UPDATE: renamed from timelineEvents
    {
      icon: MdShoppingCart,
      title: 'Order Created',
      description: `Purchase order #${po?.id} was created`,
      time: formatDateTime(po?.created_at || po?.order_date),
      color: 'blue',
    },
    ...(po?.status !== 'Pending' ? [{
      icon: MdCheckCircle,
      title: 'Order Confirmed',
      description: 'Order was confirmed and sent to supplier',
      time: formatDateTime(po?.approved_at || po?.order_date),
      color: 'green',
    }] : []),
    ...(po?.status === 'Shipped' || po?.status === 'Received' ? [{
      icon: MdLocalShipping,
      title: 'Order Shipped',
      description: 'Order has been shipped by supplier',
      time: formatDateTime(po?.shipped_at || new Date()),
      color: 'cyan',
    }] : []),
    ...(po?.status === 'Received' ? [{
      icon: MdInventory,
      title: 'Order Received',
      description: 'All items have been received',
      time: formatDateTime(po?.actual_delivery_date || new Date()),
      color: 'green',
    }] : []),
    ...(po?.status === 'Cancelled' ? [{
      icon: MdCancel,
      title: 'Order Cancelled',
      description: 'Order was cancelled',
      time: formatDateTime(po?.rejected_at || new Date()),
      color: 'red',
    }] : []),
  ];
  const timelineEvents = serverTimeline.length ? serverTimeline : derivedTimeline; // --- UPDATE

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <>
      <Modal 
        isOpen={isOpen} 
        onClose={onClose} 
        isCentered 
        size="5xl"
        scrollBehavior="inside"
        motionPreset="slideInBottom"
      >
        <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.400" />
        <ModalContent bg={modalBg} borderRadius="2xl" mx={4} overflow="hidden">
          
          {/* ================= HEADER ================= */}
          <ModalHeader
            bg={headerBg}
            borderBottom="1px solid"
            borderColor={borderColor}
            pb={4}
          >
            <Box
              h="4px"
              bgGradient={headerGradient}
              position="absolute"
              top={0}
              left={0}
              right={0}
            />
            
            <Flex justify="space-between" align="start" pt={2}>
              <HStack spacing={4}>
                <MotionBox
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', duration: 0.6 }}
                  bgGradient={headerGradient}
                  p={3}
                  borderRadius="xl"
                  boxShadow="lg"
                >
                  <Icon as={MdReceipt} color="white" boxSize={6} />
                </MotionBox>
                <VStack align="start" spacing={1}>
                  <HStack>
                    <Heading size="lg" bgGradient={headerGradient} bgClip="text">
                      Purchase Order
                    </Heading>
                    {po && (
                      <Tooltip label="Copy PO Number">
                        <Tag
                          size="lg"
                          colorScheme="blue"
                          borderRadius="full"
                          cursor="pointer"
                          onClick={handleCopyPONumber}
                          _hover={{ transform: 'scale(1.05)' }}
                          transition="all 0.2s"
                        >
                          <TagLabel fontWeight="bold">{po.po_number || `#${po.id}`}</TagLabel>
                        </Tag>
                      </Tooltip>
                    )}
                  </HStack>
                  <Text fontSize="sm" color={mutedTextColor}>
                    Enterprise Multi-Level Approval Active
                  </Text>
                  {/* --- UPDATE: who may do what */}
                  {perms?.stage && <Text fontSize="xs" color={mutedTextColor}>Next signature: {perms.stage} ({(perms.stage_roles?.[perms.stage] || []).join(', ')})</Text>}
                </VStack>
              </HStack>
              
              <HStack spacing={2}>
                {po && <AnimatedStatusBadge status={po.status} size="lg" />}
                
                <Menu>
                  <MenuButton
                    as={IconButton}
                    icon={<Icon as={MdMoreVert} />}
                    variant="ghost"
                    borderRadius="full"
                    size="sm"
                  />
                  <MenuList>
                    <MenuItem icon={<Icon as={MdPrint} />} onClick={handlePrint}>
                      Print Order
                    </MenuItem>
                    <MenuItem icon={<Icon as={MdDownload} />} onClick={handlePrint}>{/* --- UPDATE: print → Save as PDF */}
                      Download PDF
                    </MenuItem>
                    <MenuItem icon={<Icon as={MdContentCopy} />} onClick={handleCopyPONumber}>
                      Copy PO Number
                    </MenuItem>
                    <MenuDivider />
                    <MenuItem icon={<Icon as={MdEmail} />} onClick={handleEmailSupplier}>
                      Email Supplier
                    </MenuItem>
                    <MenuItem icon={<Icon as={MdShare} />} onClick={handleShare}>{/* --- UPDATE */}
                      Share
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
            </Flex>
          </ModalHeader>
          
          <ModalCloseButton 
            top={4} 
            right={4} 
            borderRadius="full"
            _hover={{ bg: 'red.100', color: 'red.500' }}
          />

          {/* ================= BODY ================= */}
          <ModalBody py={6}>
            {isLoading || !po ? (
              <DetailSkeleton />
            ) : (
              <VStack spacing={8} align="stretch">
                {/* --- UPDATE (Procurement MOW+ v1): FlowSense AI */}
                <FlowSenseInsightBar screen="po_detail" data={intel} compact
                  headline={explainPO(po, intel).split('\n').slice(0, 2)}
                  rulesAnswer={() => explainPO(po, intel)} ministryText={explainProcurementForMinistry}
                  contextBuilder={(screen, o) => buildProcurementContext(o, { purchase_order: { po_number: po.po_number, status: po.status, stage: perms?.stage, supplier: po.supplier_name, total: po.total_amount, lines: (po.items || []).map((i) => `${i.quantity} × ${i.item_name} @ ${i.unit_price ?? i.quoted_price}`), receiving: po.receiving } })}
                  questions={['Explain this order', 'Is the price right?', 'Explain purchasing for the Ministry of Works']} />
                {perms && po.status === 'Pending' && !perms.can_sign && perms.sign_block_reason && (
                  <Alert status="info" borderRadius="xl"><AlertIcon as={MdLock} />{perms.sign_block_reason}</Alert>
                )}
                {/* --- END UPDATE */}
                
                {/* Status Progress Tracker (Ordered -> Shipped -> Received) */}
                <MotionBox
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <StatusProgressTracker currentStatus={po.status} />
                </MotionBox>

                {/* ═══ MULTI-LEVEL APPROVAL HIERARCHY (Pending State) ═══ */}
                <Box p={6} bg={sectionBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} boxShadow="sm">
                    <HStack mb={4} justify="space-between">
                        <Heading size="sm" color="purple.500">Document Routing Chain</Heading>
                        {po.status === 'Pending' && (
                            <Badge colorScheme="orange" animate={{opacity:[0.5,1,0.5]}} transition="all 1s infinite">
                                Awaiting {po.approval_stage || 'Warehouse Clerk'}
                            </Badge>
                        )}
                    </HStack>
                    <Stepper index={getActiveStepIndex()} colorScheme="purple">
                        {hierarchySteps.map((step, index) => (
                            <Step key={index}>
                                <StepIndicator>
                                    <StepStatus complete={<StepIcon />} incomplete={<StepNumber />} active={<Spinner size="xs" />} />
                                </StepIndicator>
                                <Box flexShrink="0">
                                    <StepTitle>{step.title}</StepTitle>
                                    <Text fontSize="xs" color={mutedTextColor}>{step.desc}</Text>
                                </Box>
                                <StepSeparator />
                            </Step>
                        ))}
                    </Stepper>

                    {/* Signature Pad Dropdown Component */}
                    <Collapse in={showSignaturePad} animateOpacity>
                        <Box mt={6}>
                            <DigitalSignaturePad 
                                onSign={handleApplySignature} 
                                onCancel={() => setShowSignaturePad(false)} 
                                currentStage={po.approval_stage || 'Warehouse Clerk'}
                            />
                        </Box>
                    </Collapse>
                </Box>

                {/* Quick Info Cards */}
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                  <Box p={4} bg={headerBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor}>
                      <Text fontSize="xs" color={mutedTextColor} fontWeight="bold" textTransform="uppercase">Supplier</Text>
                      <Text fontSize="md" fontWeight="bold" noOfLines={1}>{po.supplier_name}</Text>
                  </Box>
                  <Box p={4} bg={headerBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor}>
                      <Text fontSize="xs" color={mutedTextColor} fontWeight="bold" textTransform="uppercase">Order Date</Text>
                      <Text fontSize="md" fontWeight="bold">{new Date(po.order_date).toLocaleDateString()}</Text>
                  </Box>
                  <Box p={4} bg={headerBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor}>
                      <Text fontSize="xs" color={mutedTextColor} fontWeight="bold" textTransform="uppercase">Total Items</Text>
                      <Text fontSize="md" fontWeight="bold">{calculateItemCount()} items</Text>
                  </Box>
                  <Box p={4} bg={totalBg} borderRadius="xl" borderWidth="1px" borderColor="green.200">{/* --- UPDATE: dark mode */}
                      <Text fontSize="xs" color="green.600" fontWeight="bold" textTransform="uppercase">Total Amount</Text>
                      <Text fontSize="lg" fontWeight="black" color="green.700">{formatCurrency(calculateTotal())}</Text>
                  </Box>
                </SimpleGrid>

                {/* Tabs Section */}
                <Tabs 
                  variant="soft-rounded" 
                  colorScheme="blue" 
                  index={activeTab}
                  onChange={setActiveTab}
                >
                  <TabList bg={headerBg} p={2} borderRadius="xl">
                    <Tab borderRadius="lg" fontWeight="600">
                      <Icon as={MdReceipt} mr={2} />
                      Items
                    </Tab>
                    <Tab borderRadius="lg" fontWeight="600">
                      <Icon as={MdThumbUp} mr={2} />
                      Signatures
                    </Tab>
                    <Tab borderRadius="lg" fontWeight="600">
                      <Icon as={MdPerson} mr={2} />
                      Supplier
                    </Tab>
                    <Tab borderRadius="lg" fontWeight="600">
                      <Icon as={MdTimeline} mr={2} />
                      Timeline
                    </Tab>
                    <Tab borderRadius="lg" fontWeight="600">
                      <Icon as={MdNote} mr={2} />
                      Notes
                    </Tab>
                  </TabList>

                  <TabPanels mt={4}>
                    {/* Items Tab */}
                    <TabPanel p={0}>
                      <MotionBox
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.1 }}
                      >
                        <Box
                          borderRadius="xl"
                          borderWidth="1px"
                          borderColor={borderColor}
                          overflow="hidden"
                        >
                          <TableContainer>
                            <Table variant="simple">
                              <Thead bg={tableHeaderBg}>
                                <Tr>
                                  <Th borderColor={borderColor}>#</Th>
                                  <Th borderColor={borderColor}>Item Name</Th>
                                  <Th isNumeric borderColor={borderColor}>Quantity</Th>
                                  <Th isNumeric borderColor={borderColor}>Received</Th>{/* --- UPDATE */}
                                  <Th isNumeric borderColor={borderColor}>Unit Price</Th>
                                  <Th isNumeric borderColor={borderColor}>Subtotal</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                <AnimatePresence>
                                  {po.items?.map((item, index) => (
                                    <MotionTr
                                      key={item.id}
                                      initial={{ opacity: 0, x: -20 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ delay: index * 0.05 }}
                                      _hover={{ bg: tableHoverBg }}
                                      bg={index % 2 === 0 ? 'transparent' : tableStripedBg}
                                    >
                                      <Td borderColor={borderColor}>
                                        <Badge colorScheme="gray" borderRadius="full">
                                          {index + 1}
                                        </Badge>
                                      </Td>
                                      <Td borderColor={borderColor}>
                                        <VStack align="start" spacing={0}>
                                          <Text fontWeight="600">{item.item_name}</Text>
                                          {item.description && (
                                            <Text fontSize="xs" color={mutedTextColor} noOfLines={1}>
                                              {item.description}
                                            </Text>
                                          )}
                                        </VStack>
                                      </Td>
                                      <Td isNumeric borderColor={borderColor}>
                                        <Text fontWeight="600">{item.quantity}</Text>
                                      </Td>
                                      {/* --- UPDATE: receiving progress */}
                                      <Td isNumeric borderColor={borderColor}>
                                        {(() => { const r = receivingById.get(String(item.id)); if (!r) return <Text color={mutedTextColor}>—</Text>;
                                          return r.remaining <= 0 ? <Badge colorScheme="green">{r.received}</Badge> : <Text fontWeight="600" color={r.received ? 'purple.500' : mutedTextColor}>{r.received} <Text as="span" fontSize="xs">({r.remaining} to come)</Text></Text>; })()}
                                      </Td>
                                      <Td isNumeric borderColor={borderColor}>
                                        <Text color={mutedTextColor}>
                                          {formatCurrency(item.unit_price || item.quoted_price)}
                                        </Text>
                                      </Td>
                                      <Td isNumeric borderColor={borderColor}>
                                        <Text fontWeight="bold" color="green.500">
                                          {formatCurrency(item.quantity * (item.unit_price || item.quoted_price))}
                                        </Text>
                                      </Td>
                                    </MotionTr>
                                  ))}
                                </AnimatePresence>
                              </Tbody>
                            </Table>
                          </TableContainer>

                          {/* Total Section */}
                          <Flex
                            justify="flex-end"
                            p={4}
                            bg={tableHeaderBg}
                            borderTop="2px solid"
                            borderColor={borderColor}
                          >
                            <VStack align="end" spacing={2}>
                              <HStack spacing={8}>
                                <Text color={mutedTextColor}>Subtotal:</Text>
                                <Text fontWeight="500">{formatCurrency(po.total_amount || calculateTotal())}</Text>
                              </HStack>
                              <HStack spacing={8}>
                                <Text color={mutedTextColor}>Tax (0%):</Text>
                                <Text fontWeight="500">{formatCurrency(0)}</Text>
                              </HStack>
                              <Divider />
                              <HStack spacing={8}>
                                <Text fontWeight="bold" fontSize="lg">Total:</Text>
                                <Text fontWeight="bold" fontSize="xl" color="green.500">
                                  {formatCurrency(po.total_amount || calculateTotal())}
                                </Text>
                              </HStack>
                            </VStack>
                          </Flex>
                        </Box>
                      </MotionBox>
                    </TabPanel>

                    {/* NEW: Signatures Tab */}
                    <TabPanel p={0}>
                      <MotionBox initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <Box borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6} bg={tableBg}>
                          <Heading size="sm" mb={4}>Cryptographic Approval Ledger</Heading>
                          {signatures.length === 0 ? (
                              <Text color={mutedTextColor} fontStyle="italic">No signatures recorded yet.</Text>
                          ) : (
                              <VStack align="stretch" spacing={4}>
                                  {signatures.map((sig, i) => (
                                      <Box key={i} p={4} bg={modalBg} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                                          <Flex justify="space-between" align="center" mb={3}>
                                              <VStack align="start" spacing={0}>
                                                  <Text fontWeight="bold">{sig.stage_name}</Text>
                                                  <Text fontSize="xs" color={mutedTextColor}>Signed by: {sig.approver_name}</Text>
                                                  <Text fontSize="xs" color={mutedTextColor}>{new Date(sig.created_at).toLocaleString()}</Text>
                                              </VStack>
                                              <Badge colorScheme="green" variant="subtle"><Icon as={MdVerified} mr={1}/> Verified</Badge>
                                          </Flex>
                                          <Box border="1px dashed" borderColor="gray.300" borderRadius="md" p={2} bg="white" w="fit-content">
                                              {/* Display the saved Base64 Signature Image */}
                                              <Image src={sig.signature_data} alt="Digital Signature" maxH="60px" />
                                          </Box>
                                          {sig.comments && (
                                              <Text fontSize="sm" mt={3} p={2} bg="gray.50" borderRadius="md" fontStyle="italic">
                                                  "{sig.comments}"
                                              </Text>
                                          )}
                                      </Box>
                                  ))}
                              </VStack>
                          )}
                      </Box>
                      </MotionBox>
                    </TabPanel>

                    {/* Supplier Tab */}
                    <TabPanel p={0}>
                      <MotionBox
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                      >
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                          <Box
                            p={6}
                            borderRadius="xl"
                            borderWidth="1px"
                            borderColor={borderColor}
                            bg={tableBg}
                          >
                            <VStack align="start" spacing={4}>
                              <HStack>
                                <Avatar 
                                  size="lg" 
                                  name={po.supplier_name} 
                                  bg="purple.500"
                                />
                                <VStack align="start" spacing={0}>
                                  <Heading size="md">{po.supplier_name}</Heading>
                                  {(po.supplier_is_verified || po.is_verified) && ( /* --- UPDATE: was shown for every supplier */
                                  <Badge colorScheme="green" borderRadius="full">
                                    <HStack spacing={1}>
                                      <Icon as={MdVerified} boxSize={3} />
                                      <Text>Verified Supplier</Text>
                                    </HStack>
                                  </Badge>
                                  )}
                                </VStack>
                              </HStack>
                              
                              <Divider />
                              
                              <VStack align="start" spacing={3} w="full">
                                <HStack>
                                  <Icon as={MdEmail} color={mutedTextColor} />
                                  <Text>{po.supplier_email || 'Not provided'}</Text>
                                </HStack>
                                <HStack>
                                  <Icon as={MdPhone} color={mutedTextColor} />
                                  <Text>{po.supplier_phone || 'Not provided'}</Text>
                                </HStack>
                                <HStack align="start">
                                  <Icon as={MdLocationOn} color={mutedTextColor} mt={1} />
                                  <Text>{po.supplier_address || 'Address not provided'}</Text>
                                </HStack>
                              </VStack>
                            </VStack>
                          </Box>

                          <Box
                            p={6}
                            borderRadius="xl"
                            borderWidth="1px"
                            borderColor={borderColor}
                            bg={tableBg}
                          >
                            <VStack align="start" spacing={4}>
                              <Heading size="sm">Payment Information</Heading>
                              <Divider />
                              
                              <SimpleGrid columns={2} spacing={4} w="full">
                                <VStack align="start" spacing={1}>
                                  <Text fontSize="xs" color={mutedTextColor}>Payment Terms</Text>
                                  <Text fontWeight="600">{po.payment_terms || '—'}</Text>{/* --- UPDATE: was an invented 'Net 30' */}
                                </VStack>
                                <VStack align="start" spacing={1}>
                                  <Text fontSize="xs" color={mutedTextColor}>Payment Method</Text>
                                  <Text fontWeight="600">{po.payment_method || '—'}</Text>{/* --- UPDATE */}
                                </VStack>
                                <VStack align="start" spacing={1}>
                                  <Text fontSize="xs" color={mutedTextColor}>Expected Delivery</Text>
                                  <Text fontWeight="600">
                                    {formatDate(po.expected_delivery_date || po.expected_delivery)}{/* --- UPDATE: the column is expected_delivery_date */}
                                  </Text>
                                </VStack>
                                <VStack align="start" spacing={1}>
                                  <Text fontSize="xs" color={mutedTextColor}>Shipping Method</Text>
                                  <Text fontWeight="600">{po.shipping_method || '—'}</Text>{/* --- UPDATE */}
                                </VStack>
                              </SimpleGrid>
                            </VStack>
                          </Box>
                        </SimpleGrid>

                        {/* Quick Actions */}
                        <HStack mt={4} spacing={3}>
                          <Button
                            leftIcon={<Icon as={MdEmail} />}
                            colorScheme="blue"
                            variant="outline"
                            borderRadius="xl"
                            onClick={handleEmailSupplier}
                          >
                            Email Supplier
                          </Button>
                          <Button
                            leftIcon={<Icon as={MdPhone} />}
                            colorScheme="green"
                            variant="outline"
                            borderRadius="xl"
                            onClick={handleCallSupplier} // --- UPDATE: was not wired
                          >
                            Call Supplier
                          </Button>
                        </HStack>
                      </MotionBox>
                    </TabPanel>

                    {/* Timeline Tab */}
                    <TabPanel p={0}>
                      <MotionBox
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                      >
                        <Box
                          p={6}
                          borderRadius="xl"
                          borderWidth="1px"
                          borderColor={borderColor}
                          bg={tableBg}
                        >
                          <HStack justify="space-between" mb={4}>
                            <Heading size="sm">Order Timeline {serverTimeline.length ? '' : '(derived from the status)'}</Heading>{/* --- UPDATE */}
                            <IconButton
                              icon={<Icon as={isTimelineOpen ? MdExpandLess : MdExpandMore} />}
                              variant="ghost"
                              size="sm"
                              onClick={onTimelineToggle}
                              borderRadius="full"
                            />
                          </HStack>
                          
                          <Collapse in={isTimelineOpen}>
                            <VStack align="stretch" spacing={0}>
                              {timelineEvents.map((event, index) => (
                                <TimelineItem
                                  key={index}
                                  icon={event.icon}
                                  title={event.title}
                                  description={event.description}
                                  time={event.time}
                                  color={event.color}
                                  isLast={index === timelineEvents.length - 1}
                                />
                              ))}
                            </VStack>
                          </Collapse>
                        </Box>
                      </MotionBox>
                    </TabPanel>

                    {/* Notes Tab */}
                    <TabPanel p={0}>
                      <MotionBox
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                      >
                        <Box
                          p={6}
                          borderRadius="xl"
                          borderWidth="1px"
                          borderColor={borderColor}
                          bg={tableBg}
                        >
                          <HStack justify="space-between" mb={4}>
                            <Heading size="sm">Order Notes</Heading>
                            {!isEditingNotes ? (
                              <Button
                                size="sm"
                                leftIcon={<Icon as={MdEdit} />}
                                variant="ghost"
                                onClick={() => setIsEditingNotes(true)}
                                borderRadius="full"
                              >
                                Edit
                              </Button>
                            ) : (
                              <HStack>
                                <Button
                                  size="sm"
                                  leftIcon={<Icon as={MdCancel} />}
                                  variant="ghost"
                                  onClick={() => {
                                    setIsEditingNotes(false);
                                    setNotes(po.notes || '');
                                  }}
                                  borderRadius="full"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  leftIcon={<Icon as={MdSave} />}
                                  colorScheme="blue"
                                  onClick={handleSaveNotes}
                                  borderRadius="full"
                                >
                                  Save
                                </Button>
                              </HStack>
                            )}
                          </HStack>
                          
                          {isEditingNotes ? (
                            <Textarea
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                              placeholder="Add notes about this purchase order..."
                              rows={6}
                              borderRadius="xl"
                              _focus={{
                                borderColor: 'blue.400',
                                boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
                              }}
                            />
                          ) : (
                            <Text color={notes ? textColor : mutedTextColor} fontStyle={notes ? 'normal' : 'italic'} whiteSpace="pre-wrap">
                              {notes || 'No notes added yet. Click Edit to add notes.'}
                            </Text>
                          )}
                        </Box>
                      </MotionBox>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </VStack>
            )}
          </ModalBody>

          {/* ================= FOOTER ================= */}
          <ModalFooter
            bg={headerBg}
            borderTop="1px solid"
            borderColor={borderColor}
            py={4}
          >
            <Flex w="full" justify="space-between" align="center" flexWrap="wrap" gap={4}>
              
              {/* Status Change Dropdown */}
              <HStack spacing={3}>
                <Text fontWeight="600" color={mutedTextColor}>
                  Change Status:
                </Text>
                <Tooltip 
                   label={po?.status === 'Pending' ? "Formal approval required before manual status updates." : ""} 
                   hasArrow 
                   placement="top"
                >
                  <Box>
                    <Select
                      w="160px"
                      value={po?.status || ''}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      // Lock dropdown if Pending, Received, or Cancelled
                      isDisabled={
                          isLoading || 
                          isUpdating || 
                          po?.status === 'Pending' || 
                          po?.status === 'Received' || 
                          po?.status === 'Cancelled'
                      }
                      borderRadius="xl"
                      fontWeight="600"
                      bg={selectBg}
                    >
                      {po?.status === 'Pending' && <option value="Pending">Pending</option>}
                      {po?.status === 'Ordered' && (
                          <>
                              <option value="Ordered">Ordered</option>
                              <option value="Shipped">Shipped</option>
                              <option value="Cancelled">Cancel Order</option>
                          </>
                      )}
                      {po?.status === 'Shipped' && (
                          <>
                              <option value="Shipped">Shipped</option>
                              <option value="Received">Received</option>
                          </>
                      )}
                      {/* --- UPDATE: partly received orders */}
                      {po?.status === 'Partial' && (
                          <>
                              <option value="Partial">Partly received</option>
                              <option value="Received">Received (all outstanding)</option>
                          </>
                      )}
                      {po?.status === 'Received' && <option value="Received">Received</option>}
                      {po?.status === 'Cancelled' && <option value="Cancelled">Cancelled</option>}
                    </Select>
                  </Box>
                </Tooltip>
                {isUpdating && <Spinner size="sm" color="blue.500" />}
              </HStack>

              {/* Action Buttons */}
              <HStack spacing={3}>
                
                {/* --- DIGITAL SIGNATURE & APPROVAL BUTTONS --- */}
                {po?.status === 'Pending' && !showSignaturePad && (
                    <Tooltip label={!canSign ? perms?.sign_block_reason : ''} hasArrow>{/* --- UPDATE: only when you may sign this stage */}
                    <Button colorScheme="purple" leftIcon={<Icon as={MdDraw} />}
                            isLoading={isUpdating}
                            isDisabled={!canSign}
                            onClick={() => setShowSignaturePad(true)}
                            boxShadow="0 4px 14px 0 rgba(128,90,213,0.39)">
                        Sign as {perms?.stage || po.approval_stage || 'Warehouse Clerk'}
                    </Button>
                    </Tooltip>
                )}
                {/* --- UPDATE: goods receipt */}
                {canReceive && (
                    <Button colorScheme="green" leftIcon={<Icon as={MdInventory} />} onClick={() => setReceiveOpen(true)}>Receive goods</Button>
                )}

                {/* Legacy Authorize Fallback (Optional) */}
                {po?.status === 'Pending' && (canApprove || canReject) && ( /* --- UPDATE: per permissions */
                  <>
                    {canApprove && (
                    <Tooltip label="Manager override: approve without the remaining signatures" hasArrow>
                    <Button colorScheme="green" leftIcon={<Icon as={MdThumbUp} />}
                            isLoading={isUpdating}
                            onClick={handleApprove}
                            boxShadow="0 4px 14px 0 rgba(72,187,120,0.39)">
                      Authorize
                    </Button>
                    </Tooltip>
                    )}
                    {canReject && (
                    <Button colorScheme="red" variant="outline" leftIcon={<Icon as={MdCancel} />}
                            onClick={onRejectOpen}>
                      Reject
                    </Button>
                    )}
                    <Divider orientation="vertical" h="24px" mx={2} />
                  </>
                )}
                
                <Tooltip label="Print Order">
                  <IconButton
                    icon={<Icon as={MdPrint} />}
                    variant="ghost"
                    borderRadius="full"
                    onClick={handlePrint}
                    aria-label="Print order"
                  />
                </Tooltip>
                <Tooltip label="Download PDF">
                  <IconButton
                    icon={<Icon as={MdDownload} />}
                    variant="ghost"
                    borderRadius="full"
                    onClick={handlePrint} // --- UPDATE: was not wired
                    aria-label="Download PDF"
                  />
                </Tooltip>
                <Tooltip label="Email Supplier">
                  <IconButton
                    icon={<Icon as={MdEmail} />}
                    variant="ghost"
                    borderRadius="full"
                    onClick={handleEmailSupplier}
                    aria-label="Email supplier"
                  />
                </Tooltip>
                <Button
                  onClick={onClose}
                  borderRadius="xl"
                  px={6}
                >
                  Close
                </Button>
              </HStack>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* --- UPDATE: goods receipt */}
      <ReceiveGoodsModal isOpen={receiveOpen} onClose={() => setReceiveOpen(false)} po={po} insights={insights}
        onDone={(r) => { toast({ title: r.complete ? 'Order received' : 'Goods received', description: r.received.map((x) => `${x.quantity} × ${x.item_name}`).join(', '), status: 'success', duration: 4000, position: 'top-right' }); onStatusUpdate?.(); fetchPODetails(true); }} />
      {/* --- REJECT CONFIRMATION DIALOG --- */}
      <AlertDialog
        isOpen={isRejectOpen}
        leastDestructiveRef={cancelRef}
        onClose={onRejectClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="2xl">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Reject Purchase Order
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text mb={4}>
                Are you sure you want to reject this purchase order? This action cannot be undone.
              </Text>
              <FormControl>
                <FormLabel fontWeight="bold">Reason for Rejection</FormLabel>
                <Textarea 
                   placeholder="Enter the reason for rejecting this order (optional)..." 
                   value={rejectReason}
                   onChange={(e) => setRejectReason(e.target.value)}
                   borderRadius="xl"
                />
              </FormControl>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onRejectClose} borderRadius="xl">
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleReject} ml={3} isLoading={isUpdating} borderRadius="xl">
                Confirm Rejection
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default PurchaseOrderDetailModal;